import { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/molecules/Card';
import { Button } from '@/components/atoms/Button';
import { Switch } from '@/components/atoms/Switch';
import { Select } from '@/components/atoms/Select';
import { Spinner } from '@/components/atoms/Spinner';
import { Bell, Shield, Database, Activity, Save, Calendar } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { ApiClient } from '@/services/api/client';
import styles from './SettingsPage.module.scss';

const DEFAULT_NOTIFICATIONS = {
  emailNotifications: true,
  newUserAlerts: true,
  newCompanyAlerts: true,
  contentReportAlerts: true,
  systemErrorAlerts: true,
  dailyReports: true,
};

const DEFAULT_SECURITY = {
  twoFactorAuth: true,
  sessionTimeout: '30',
  requirePasswordChange: false,
  logFailedLoginAttempts: true,
};

const DEFAULT_SYSTEM = {
  maintenanceMode: false,
  debugMode: false,
  logLevel: 'info',
  dataRetention: '90',
};

const DEFAULT_MONITORING = {
  errorTracking: true,
  performanceMonitoring: true,
  userActivityLogs: true,
  apiRateLimiting: true,
};

const DEFAULT_WEEKLY_STORIES = {
  storiesPerWeek: '5',
  assignmentDay: '1', // 1 = Monday
  assignmentTime: '00:00',
};

export function SettingsPage() {
  const { user } = useAuth();
  const [notificationSettings, setNotificationSettings] = useState(DEFAULT_NOTIFICATIONS);
  const [securitySettings, setSecuritySettings] = useState(DEFAULT_SECURITY);
  const [systemSettings, setSystemSettings] = useState(DEFAULT_SYSTEM);
  const [monitoringSettings, setMonitoringSettings] = useState(DEFAULT_MONITORING);
  const [weeklyStoriesSettings, setWeeklyStoriesSettings] = useState(DEFAULT_WEEKLY_STORIES);

  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadSettings = async () => {
      if (!user?.id) {
        setIsLoading(false);
        return;
      }
      try {
        const prefs = (await ApiClient.get<Record<string, unknown>>('/users/me/preferences').catch(
          (): Record<string, unknown> => ({})
        ));
        setNotificationSettings({
          emailNotifications: prefs['emailNotifications'] !== undefined ? (prefs['emailNotifications'] as boolean) : DEFAULT_NOTIFICATIONS.emailNotifications,
          newUserAlerts: prefs['newUserAlerts'] !== undefined ? (prefs['newUserAlerts'] as boolean) : DEFAULT_NOTIFICATIONS.newUserAlerts,
          newCompanyAlerts: prefs['newCompanyAlerts'] !== undefined ? (prefs['newCompanyAlerts'] as boolean) : DEFAULT_NOTIFICATIONS.newCompanyAlerts,
          contentReportAlerts: prefs['contentReportAlerts'] !== undefined ? (prefs['contentReportAlerts'] as boolean) : DEFAULT_NOTIFICATIONS.contentReportAlerts,
          systemErrorAlerts: prefs['systemErrorAlerts'] !== undefined ? (prefs['systemErrorAlerts'] as boolean) : DEFAULT_NOTIFICATIONS.systemErrorAlerts,
          dailyReports: prefs['dailyReports'] !== undefined ? (prefs['dailyReports'] as boolean) : DEFAULT_NOTIFICATIONS.dailyReports,
        });
        setSecuritySettings({
          twoFactorAuth: prefs['twoFactorAuth'] !== undefined ? (prefs['twoFactorAuth'] as boolean) : DEFAULT_SECURITY.twoFactorAuth,
          sessionTimeout: prefs['sessionTimeout'] !== undefined ? (prefs['sessionTimeout'] as string) : DEFAULT_SECURITY.sessionTimeout,
          requirePasswordChange: prefs['requirePasswordChange'] !== undefined ? (prefs['requirePasswordChange'] as boolean) : DEFAULT_SECURITY.requirePasswordChange,
          logFailedLoginAttempts: prefs['logFailedLoginAttempts'] !== undefined ? (prefs['logFailedLoginAttempts'] as boolean) : DEFAULT_SECURITY.logFailedLoginAttempts,
        });
        setSystemSettings({
          maintenanceMode: prefs['maintenanceMode'] !== undefined ? (prefs['maintenanceMode'] as boolean) : DEFAULT_SYSTEM.maintenanceMode,
          debugMode: prefs['debugMode'] !== undefined ? (prefs['debugMode'] as boolean) : DEFAULT_SYSTEM.debugMode,
          logLevel: prefs['logLevel'] !== undefined ? (prefs['logLevel'] as string) : DEFAULT_SYSTEM.logLevel,
          dataRetention: prefs['dataRetention'] !== undefined ? (prefs['dataRetention'] as string) : DEFAULT_SYSTEM.dataRetention,
        });
        setMonitoringSettings({
          errorTracking: prefs['errorTracking'] !== undefined ? (prefs['errorTracking'] as boolean) : DEFAULT_MONITORING.errorTracking,
          performanceMonitoring: prefs['performanceMonitoring'] !== undefined ? (prefs['performanceMonitoring'] as boolean) : DEFAULT_MONITORING.performanceMonitoring,
          userActivityLogs: prefs['userActivityLogs'] !== undefined ? (prefs['userActivityLogs'] as boolean) : DEFAULT_MONITORING.userActivityLogs,
          apiRateLimiting: prefs['apiRateLimiting'] !== undefined ? (prefs['apiRateLimiting'] as boolean) : DEFAULT_MONITORING.apiRateLimiting,
        });
        setWeeklyStoriesSettings({
          storiesPerWeek: prefs['storiesPerWeek'] !== undefined ? (prefs['storiesPerWeek'] as string) : DEFAULT_WEEKLY_STORIES.storiesPerWeek,
          assignmentDay: prefs['assignmentDay'] !== undefined ? (prefs['assignmentDay'] as string) : DEFAULT_WEEKLY_STORIES.assignmentDay,
          assignmentTime: prefs['assignmentTime'] !== undefined ? (prefs['assignmentTime'] as string) : DEFAULT_WEEKLY_STORIES.assignmentTime,
        });
      } catch (error) {
        console.error('Failed to load backoffice settings:', error);
      } finally {
        setIsLoading(false);
      }
    };

    void loadSettings();
  }, [user?.id]);

  const handleSaveSettings = async () => {
    if (!user?.id) return;
    setIsSaving(true);
    try {
      const payload: Record<string, unknown> = {
        emailNotifications: notificationSettings.emailNotifications,
        newUserAlerts: notificationSettings.newUserAlerts,
        newCompanyAlerts: notificationSettings.newCompanyAlerts,
        contentReportAlerts: notificationSettings.contentReportAlerts,
        systemErrorAlerts: notificationSettings.systemErrorAlerts,
        dailyReports: notificationSettings.dailyReports,
        twoFactorAuth: securitySettings.twoFactorAuth,
        sessionTimeout: securitySettings.sessionTimeout,
        requirePasswordChange: securitySettings.requirePasswordChange,
        logFailedLoginAttempts: securitySettings.logFailedLoginAttempts,
        maintenanceMode: systemSettings.maintenanceMode,
        debugMode: systemSettings.debugMode,
        logLevel: systemSettings.logLevel,
        dataRetention: systemSettings.dataRetention,
        errorTracking: monitoringSettings.errorTracking,
        performanceMonitoring: monitoringSettings.performanceMonitoring,
        userActivityLogs: monitoringSettings.userActivityLogs,
        apiRateLimiting: monitoringSettings.apiRateLimiting,
        storiesPerWeek: weeklyStoriesSettings.storiesPerWeek,
        assignmentDay: weeklyStoriesSettings.assignmentDay,
        assignmentTime: weeklyStoriesSettings.assignmentTime,
      };
      const response = await ApiClient.patch<Record<string, unknown>>('/users/me/preferences', payload);
      setNotificationSettings({
        emailNotifications: response.emailNotifications !== undefined ? (response.emailNotifications as boolean) : DEFAULT_NOTIFICATIONS.emailNotifications,
        newUserAlerts: response.newUserAlerts !== undefined ? (response.newUserAlerts as boolean) : DEFAULT_NOTIFICATIONS.newUserAlerts,
        newCompanyAlerts: response.newCompanyAlerts !== undefined ? (response.newCompanyAlerts as boolean) : DEFAULT_NOTIFICATIONS.newCompanyAlerts,
        contentReportAlerts: response.contentReportAlerts !== undefined ? (response.contentReportAlerts as boolean) : DEFAULT_NOTIFICATIONS.contentReportAlerts,
        systemErrorAlerts: response.systemErrorAlerts !== undefined ? (response.systemErrorAlerts as boolean) : DEFAULT_NOTIFICATIONS.systemErrorAlerts,
        dailyReports: response.dailyReports !== undefined ? (response.dailyReports as boolean) : DEFAULT_NOTIFICATIONS.dailyReports,
      });
      setSecuritySettings({
        twoFactorAuth: response.twoFactorAuth !== undefined ? (response.twoFactorAuth as boolean) : DEFAULT_SECURITY.twoFactorAuth,
        sessionTimeout: response.sessionTimeout !== undefined ? (response.sessionTimeout as string) : DEFAULT_SECURITY.sessionTimeout,
        requirePasswordChange: response.requirePasswordChange !== undefined ? (response.requirePasswordChange as boolean) : DEFAULT_SECURITY.requirePasswordChange,
        logFailedLoginAttempts: response.logFailedLoginAttempts !== undefined ? (response.logFailedLoginAttempts as boolean) : DEFAULT_SECURITY.logFailedLoginAttempts,
      });
      setSystemSettings({
        maintenanceMode: response.maintenanceMode !== undefined ? (response.maintenanceMode as boolean) : DEFAULT_SYSTEM.maintenanceMode,
        debugMode: response.debugMode !== undefined ? (response.debugMode as boolean) : DEFAULT_SYSTEM.debugMode,
        logLevel: response.logLevel !== undefined ? (response.logLevel as string) : DEFAULT_SYSTEM.logLevel,
        dataRetention: response.dataRetention !== undefined ? (response.dataRetention as string) : DEFAULT_SYSTEM.dataRetention,
      });
      setMonitoringSettings({
        errorTracking: response.errorTracking !== undefined ? (response.errorTracking as boolean) : DEFAULT_MONITORING.errorTracking,
        performanceMonitoring: response.performanceMonitoring !== undefined ? (response.performanceMonitoring as boolean) : DEFAULT_MONITORING.performanceMonitoring,
        userActivityLogs: response.userActivityLogs !== undefined ? (response.userActivityLogs as boolean) : DEFAULT_MONITORING.userActivityLogs,
        apiRateLimiting: response.apiRateLimiting !== undefined ? (response.apiRateLimiting as boolean) : DEFAULT_MONITORING.apiRateLimiting,
      });
      setWeeklyStoriesSettings({
        storiesPerWeek: response.storiesPerWeek !== undefined ? (response.storiesPerWeek as string) : DEFAULT_WEEKLY_STORIES.storiesPerWeek,
        assignmentDay: response.assignmentDay !== undefined ? (response.assignmentDay as string) : DEFAULT_WEEKLY_STORIES.assignmentDay,
        assignmentTime: response.assignmentTime !== undefined ? (response.assignmentTime as string) : DEFAULT_WEEKLY_STORIES.assignmentTime,
      });
    } catch (error) {
      console.error('Failed to save backoffice settings:', error);
      alert('Error al guardar las preferencias. Por favor, intenta de nuevo.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className={styles.settings}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem', padding: '2rem' }}>
          <Spinner size="lg" />
          <p>Cargando configuración...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.settings}>
      <div className={styles.settings__header}>
        <div>
          <h1 className={styles.settings__title}>Configuración</h1>
          <p className={styles.settings__subtitle}>Configura las preferencias del sistema y de administración</p>
        </div>
        <Button
          variant="primary"
          onClick={handleSaveSettings}
          isLoading={isSaving}
          leftIcon={<Save size={16} />}
        >
          Guardar cambios
        </Button>
      </div>

      <div className={styles.settings__content}>
        <Card>
          <CardHeader>
            <div className={styles.settings__cardHeader}>
              <Bell className={styles.settings__cardIcon} />
              <CardTitle>Preferencias de notificaciones</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className={styles.settings__section}>
              <div className={styles.settings__item}>
                <div className={styles.settings__itemInfo}>
                  <span className={styles.settings__itemLabel}>Notificaciones por email</span>
                  <span className={styles.settings__itemDescription}>
                    Recibe alertas por email sobre eventos importantes del sistema
                  </span>
                </div>
                <Switch
                  checked={notificationSettings.emailNotifications}
                  onCheckedChange={(checked) =>
                    setNotificationSettings({ ...notificationSettings, emailNotifications: checked })
                  }
                />
              </div>

              <div className={styles.settings__item}>
                <div className={styles.settings__itemInfo}>
                  <span className={styles.settings__itemLabel}>Alertas de nuevos usuarios</span>
                  <span className={styles.settings__itemDescription}>
                    Recibe notificaciones cuando nuevos usuarios se registren en la plataforma
                  </span>
                </div>
                <Switch
                  checked={notificationSettings.newUserAlerts}
                  onCheckedChange={(checked) =>
                    setNotificationSettings({ ...notificationSettings, newUserAlerts: checked })
                  }
                />
              </div>

              <div className={styles.settings__item}>
                <div className={styles.settings__itemInfo}>
                  <span className={styles.settings__itemLabel}>Alertas de nuevas empresas</span>
                  <span className={styles.settings__itemDescription}>
                    Recibe alertas cuando nuevas empresas se registren
                  </span>
                </div>
                <Switch
                  checked={notificationSettings.newCompanyAlerts}
                  onCheckedChange={(checked) =>
                    setNotificationSettings({ ...notificationSettings, newCompanyAlerts: checked })
                  }
                />
              </div>

              <div className={styles.settings__item}>
                <div className={styles.settings__itemInfo}>
                  <span className={styles.settings__itemLabel}>Alertas de contenido reportado</span>
                  <span className={styles.settings__itemDescription}>
                    Recibe notificaciones sobre problemas de contenido reportados por usuarios
                  </span>
                </div>
                <Switch
                  checked={notificationSettings.contentReportAlerts}
                  onCheckedChange={(checked) =>
                    setNotificationSettings({ ...notificationSettings, contentReportAlerts: checked })
                  }
                />
              </div>

              <div className={styles.settings__item}>
                <div className={styles.settings__itemInfo}>
                  <span className={styles.settings__itemLabel}>Alertas de errores del sistema</span>
                  <span className={styles.settings__itemDescription}>
                    Recibe alertas inmediatas ante errores críticos del sistema
                  </span>
                </div>
                <Switch
                  checked={notificationSettings.systemErrorAlerts}
                  onCheckedChange={(checked) =>
                    setNotificationSettings({ ...notificationSettings, systemErrorAlerts: checked })
                  }
                />
              </div>

              <div className={styles.settings__item}>
                <div className={styles.settings__itemInfo}>
                  <span className={styles.settings__itemLabel}>Informes diarios</span>
                  <span className={styles.settings__itemDescription}>
                    Recibe resúmenes diarios de la actividad de la plataforma
                  </span>
                </div>
                <Switch
                  checked={notificationSettings.dailyReports}
                  onCheckedChange={(checked) =>
                    setNotificationSettings({ ...notificationSettings, dailyReports: checked })
                  }
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className={styles.settings__cardHeader}>
              <Shield className={styles.settings__cardIcon} />
              <CardTitle>Configuración de seguridad</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className={styles.settings__section}>
              <div className={styles.settings__item}>
                <div className={styles.settings__itemInfo}>
                  <span className={styles.settings__itemLabel}>Autenticación de dos factores</span>
                  <span className={styles.settings__itemDescription}>
                    Requerir 2FA para todas las cuentas de administrador
                  </span>
                </div>
                <Switch
                  checked={securitySettings.twoFactorAuth}
                  onCheckedChange={(checked) =>
                    setSecuritySettings({ ...securitySettings, twoFactorAuth: checked })
                  }
                />
              </div>

              <div className={styles.settings__item}>
                <div className={styles.settings__itemInfo}>
                  <span className={styles.settings__itemLabel}>Tiempo de espera de sesión</span>
                  <span className={styles.settings__itemDescription}>
                    Cierre de sesión automático por inactividad (minutos)
                  </span>
                </div>
                <Select
                  value={securitySettings.sessionTimeout}
                  onValueChange={(value) =>
                    setSecuritySettings({ ...securitySettings, sessionTimeout: value })
                  }
                  options={[
                    { value: '15', label: '15 minutos' },
                    { value: '30', label: '30 minutos' },
                    { value: '60', label: '1 hora' },
                    { value: '120', label: '2 horas' },
                  ]}
                />
              </div>

              <div className={styles.settings__item}>
                <div className={styles.settings__itemInfo}>
                  <span className={styles.settings__itemLabel}>Requerir cambio periódico de contraseña</span>
                  <span className={styles.settings__itemDescription}>
                    Forzar actualización de contraseña cada 90 días
                  </span>
                </div>
                <Switch
                  checked={securitySettings.requirePasswordChange}
                  onCheckedChange={(checked) =>
                    setSecuritySettings({ ...securitySettings, requirePasswordChange: checked })
                  }
                />
              </div>

              <div className={styles.settings__item}>
                <div className={styles.settings__itemInfo}>
                  <span className={styles.settings__itemLabel}>Registrar intentos fallidos de inicio de sesión</span>
                  <span className={styles.settings__itemDescription}>
                    Rastrear y registrar todos los intentos de autenticación fallidos
                  </span>
                </div>
                <Switch
                  checked={securitySettings.logFailedLoginAttempts}
                  onCheckedChange={(checked) =>
                    setSecuritySettings({ ...securitySettings, logFailedLoginAttempts: checked })
                  }
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className={styles.settings__cardHeader}>
              <Database className={styles.settings__cardIcon} />
              <CardTitle>Configuración del sistema</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className={styles.settings__section}>
              <div className={styles.settings__item}>
                <div className={styles.settings__itemInfo}>
                  <span className={styles.settings__itemLabel}>Modo mantenimiento</span>
                  <span className={styles.settings__itemDescription}>
                    Poner la plataforma en modo mantenimiento (los usuarios no podrán acceder)
                  </span>
                </div>
                <Switch
                  checked={systemSettings.maintenanceMode}
                  onCheckedChange={(checked) =>
                    setSystemSettings({ ...systemSettings, maintenanceMode: checked })
                  }
                />
              </div>

              <div className={styles.settings__item}>
                <div className={styles.settings__itemInfo}>
                  <span className={styles.settings__itemLabel}>Modo depuración</span>
                  <span className={styles.settings__itemDescription}>
                    Habilitar registro detallado de errores e información de depuración
                  </span>
                </div>
                <Switch
                  checked={systemSettings.debugMode}
                  onCheckedChange={(checked) =>
                    setSystemSettings({ ...systemSettings, debugMode: checked })
                  }
                />
              </div>

              <div className={styles.settings__item}>
                <div className={styles.settings__itemInfo}>
                  <span className={styles.settings__itemLabel}>Nivel de log</span>
                  <span className={styles.settings__itemDescription}>
                    Establece el nivel de detalle de los logs del sistema
                  </span>
                </div>
                <Select
                  value={systemSettings.logLevel}
                  onValueChange={(value) =>
                    setSystemSettings({ ...systemSettings, logLevel: value })
                  }
                  options={[
                    { value: 'error', label: 'Solo errores' },
                    { value: 'warn', label: 'Advertencias' },
                    { value: 'info', label: 'Info' },
                    { value: 'debug', label: 'Debug' },
                    { value: 'verbose', label: 'Detallado' },
                  ]}
                />
              </div>

              <div className={styles.settings__item}>
                <div className={styles.settings__itemInfo}>
                  <span className={styles.settings__itemLabel}>Período de retención de datos</span>
                  <span className={styles.settings__itemDescription}>
                    Tiempo de conservación de los logs de actividad de usuarios (días)
                  </span>
                </div>
                <Select
                  value={systemSettings.dataRetention}
                  onValueChange={(value) =>
                    setSystemSettings({ ...systemSettings, dataRetention: value })
                  }
                  options={[
                    { value: '30', label: '30 días' },
                    { value: '90', label: '90 días' },
                    { value: '180', label: '180 días' },
                    { value: '365', label: '1 año' },
                  ]}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className={styles.settings__cardHeader}>
              <Activity className={styles.settings__cardIcon} />
              <CardTitle>Monitorización y analíticas</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className={styles.settings__section}>
              <div className={styles.settings__item}>
                <div className={styles.settings__itemInfo}>
                  <span className={styles.settings__itemLabel}>Seguimiento de errores</span>
                  <span className={styles.settings__itemDescription}>
                    Monitorizar y rastrear errores de la aplicación en tiempo real
                  </span>
                </div>
                <Switch
                  checked={monitoringSettings.errorTracking}
                  onCheckedChange={(checked) =>
                    setMonitoringSettings({ ...monitoringSettings, errorTracking: checked })
                  }
                />
              </div>

              <div className={styles.settings__item}>
                <div className={styles.settings__itemInfo}>
                  <span className={styles.settings__itemLabel}>Monitorización de rendimiento</span>
                  <span className={styles.settings__itemDescription}>
                    Rastrear métricas de rendimiento y cuellos de botella de la aplicación
                  </span>
                </div>
                <Switch
                  checked={monitoringSettings.performanceMonitoring}
                  onCheckedChange={(checked) =>
                    setMonitoringSettings({ ...monitoringSettings, performanceMonitoring: checked })
                  }
                />
              </div>

              <div className={styles.settings__item}>
                <div className={styles.settings__itemInfo}>
                  <span className={styles.settings__itemLabel}>Logs de actividad de usuarios</span>
                  <span className={styles.settings__itemDescription}>
                    Registrar todas las acciones de usuarios para auditoría
                  </span>
                </div>
                <Switch
                  checked={monitoringSettings.userActivityLogs}
                  onCheckedChange={(checked) =>
                    setMonitoringSettings({ ...monitoringSettings, userActivityLogs: checked })
                  }
                />
              </div>

              <div className={styles.settings__item}>
                <div className={styles.settings__itemInfo}>
                  <span className={styles.settings__itemLabel}>Límite de peticiones API</span>
                  <span className={styles.settings__itemDescription}>
                    Aplicar límites de peticiones en los endpoints de la API para evitar abusos
                  </span>
                </div>
                <Switch
                  checked={monitoringSettings.apiRateLimiting}
                  onCheckedChange={(checked) =>
                    setMonitoringSettings({ ...monitoringSettings, apiRateLimiting: checked })
                  }
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className={styles.settings__cardHeader}>
              <Calendar className={styles.settings__cardIcon} />
              <CardTitle>Configuración de historias semanales</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className={styles.settings__section}>
              <div className={styles.settings__item}>
                <div className={styles.settings__itemInfo}>
                  <span className={styles.settings__itemLabel}>Historias por semana</span>
                  <span className={styles.settings__itemDescription}>
                    Número de historias asignadas automáticamente a cada estudiante cada semana
                  </span>
                </div>
                <Select
                  value={weeklyStoriesSettings.storiesPerWeek}
                  onValueChange={(value) =>
                    setWeeklyStoriesSettings({ ...weeklyStoriesSettings, storiesPerWeek: value })
                  }
                  options={[
                    { value: '3', label: '3 historias' },
                    { value: '5', label: '5 historias' },
                    { value: '7', label: '7 historias' },
                    { value: '10', label: '10 historias' },
                  ]}
                />
              </div>

              <div className={styles.settings__item}>
                <div className={styles.settings__itemInfo}>
                  <span className={styles.settings__itemLabel}>Día de asignación</span>
                  <span className={styles.settings__itemDescription}>
                    Día de la semana en que se asignan las nuevas historias
                  </span>
                </div>
                <Select
                  value={weeklyStoriesSettings.assignmentDay}
                  onValueChange={(value) =>
                    setWeeklyStoriesSettings({ ...weeklyStoriesSettings, assignmentDay: value })
                  }
                  options={[
                    { value: '0', label: 'Domingo' },
                    { value: '1', label: 'Lunes' },
                    { value: '2', label: 'Martes' },
                  ]}
                />
              </div>

              <div className={styles.settings__item}>
                <div className={styles.settings__itemInfo}>
                  <span className={styles.settings__itemLabel}>Hora de asignación</span>
                  <span className={styles.settings__itemDescription}>
                    Hora del día en que se asignan las historias (formato 24 horas)
                  </span>
                </div>
                <Select
                  value={weeklyStoriesSettings.assignmentTime}
                  onValueChange={(value) =>
                    setWeeklyStoriesSettings({ ...weeklyStoriesSettings, assignmentTime: value })
                  }
                  options={[
                    { value: '00:00', label: '00:00 (Medianoche)' },
                    { value: '06:00', label: '06:00' },
                    { value: '09:00', label: '09:00' },
                    { value: '12:00', label: '12:00 (Mediodía)' },
                  ]}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default SettingsPage;
